import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractWidgetComponent, ContextChange, DialogService, DictionaryEntry, MasterDetailListEntry, ResponseMessage, ResponseStatus, TranslationService, UserProfileDialogService, WidgetStyle } from '@sdbn/common';
import { Subscription } from 'rxjs';

import { EquipmentIdentifier } from '@sdbn/tmc-common';
import { ParameterType } from '../model/parameter-type.enum';
import { SupplementaryEquipment } from '../model/supplementary-equipment';
import { SupplementaryEquipmentDetails } from '../model/supplementary-equipment-details';
import { SupplementaryEquipmentService } from '../service/supplementary-equipment.service';
import { SupplementaryEquipmentIdentifier } from './../model/supplementary-equipment-details';

export interface ParameterTypeOption {
  option: ParameterType;
  name: string;
}

@Component({
    selector: 'app-supplementary-equipment-admin',
    templateUrl: './supplementary-equipment.component.html',
    styleUrls: ['./supplementary-equipment.component.scss'],
    standalone: false
})
export class SupplementaryEquipmentAdministrationComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  parameterTypeOptions: ParameterTypeOption[] = [];

  euqipment: SupplementaryEquipment[] = [];

  equipmentIdentifier: EquipmentIdentifier;

  isLoading = false;

  selectedEquipment: SupplementaryEquipment;
  selectedDetails: SupplementaryEquipmentDetails;
  workingCopyDetails: SupplementaryEquipmentDetails;

  responseMessages: ResponseMessage[];

  languageSubscription: Subscription;

  didIdentifiersChange = false;

  constructor(
    public translationService: TranslationService,
    private ses: SupplementaryEquipmentService,
    public userProfileDialogService: UserProfileDialogService,
    private dialogService: DialogService
  ) {

    super();

    for (const k of Object.keys(ParameterType)) {
      this.parameterTypeOptions.push({ option: ParameterType[k], name: k });
    }

    this.selectedDetails = this.createNewDetails();
    this.workingCopyDetails = JSON.parse(JSON.stringify(this.selectedDetails));

    this.languageSubscription = this.translationService.changed.subscribe(language => {

      this.createTranslatedValuesOnList();

    });
  }

  createTranslatedValuesOnList() {
    for (const supplementaryEquipment of this.euqipment) {
      supplementaryEquipment.translatedLabel = this.translationService.translate(supplementaryEquipment.dictionaryKey);
    }
  }

  ngOnDestroy(): void {

    super.ngOnDestroy();

    if (this.languageSubscription != null) {
      this.languageSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.initCreation();
    this.load();
  }

  isSelected(details: SupplementaryEquipment) {
    return this.selectedDetails && this.selectedDetails.id === details.id;
  }

  setSelectedWithoutCheck(equipment: SupplementaryEquipment) {
    this.isLoading = true;
    this.ses.getSupplementaryEquipmentDetails({ id: equipment.id }).subscribe(details => {
      console.log("DATA", details);
      this.selectedEquipment = equipment;
      this.selectedDetails = details;
      this.workingCopyDetails = JSON.parse(JSON.stringify(details));
      this.isLoading = false;
    }, error => {
      this.isLoading = false;
      console.log(error);
    });
  }

  setSelected(euqipment: SupplementaryEquipment) {

    if (this.selectedEquipment && this.selectedEquipment.id === euqipment.id) {
      return;
    }

    this.isLoading = true;

    if (euqipment != null) {
      this.ses.getSupplementaryEquipmentDetails({ id: euqipment.id }).subscribe(details => {

        if (this.isDirty()) {

          this.dialogService.showConfirm(
            'tmc.admin.supplementary-equipment.discard.confirm-dialog.title',
            'tmc.admin.supplementary-equipment.discard.confirm-dialog.message'
          ).afterClosed().subscribe(confirm => {
            if (confirm) {
              this.selectedEquipment = euqipment;
              this.selectedDetails = details;
              this.workingCopyDetails = JSON.parse(JSON.stringify(details));
              this.responseMessages = [];
            }
          });

        } else {
          this.selectedEquipment = euqipment;
          this.selectedDetails = details;
          this.workingCopyDetails = JSON.parse(JSON.stringify(details));
          this.responseMessages = [];
        }
        this.isLoading = false;
      }, error => {
        console.log(error);
        this.isLoading = false;
      });
    } else {
      this.isLoading = false;
    }
  }

  delete(euqipment: SupplementaryEquipment) {

    this.dialogService.showConfirm(
      'tmc.admin.supplementary-equipment.delete.confirm-dialog.title',
      'tmc.admin.supplementary-equipment.delete.confirm-dialog.message'
    ).afterClosed().subscribe(confirm => {
      if (confirm) {
        this.responseMessages = [];
        this.isLoading = true;
        this.ses.deleteSupplementaryEquipment({ id: euqipment.id }).subscribe(response => {

          if (response.status === ResponseStatus.SUCCESS) {
            this.clear();
            this.load();
          } else {
            this.responseMessages = response.responseMessages;
          }

        }, error => {
          console.log(error);
          this.isLoading = false;
        });
      }
    });

  }

  save() {
    if (this.workingCopyDetails.id) {
      this.saveEquipment(this.workingCopyDetails);
    } else {
      this.createEquipment(this.workingCopyDetails);
    }
  }

  createEquipment(details: SupplementaryEquipmentDetails) {
    this.isLoading = true;
    this.responseMessages = [];
    this.ses.saveSupplementaryEquipment(details).subscribe(response => {
      this.isLoading = false;
      if (response.status === ResponseStatus.SUCCESS) {
        this.didIdentifiersChange = false;
        this.clear();
        this.load();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.isLoading = false;
    });
  }

  saveEquipment(details: SupplementaryEquipmentDetails) {
    this.isLoading = true;
    this.responseMessages = [];
    this.ses.saveSupplementaryEquipment(details).subscribe(response => {
      this.isLoading = false;
      if (response.status === ResponseStatus.SUCCESS) {
        this.didIdentifiersChange = false;
        this.selectedDetails = JSON.parse(JSON.stringify(this.workingCopyDetails));
        this.load();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.isLoading = false;
    });
  }

  clear() {
    this.selectedEquipment = null;
    this.selectedDetails = this.createNewDetails();
    this.workingCopyDetails = JSON.parse(JSON.stringify(this.selectedDetails));
  }

  addNew() {

    if (this.isDirty()) {

      this.dialogService.showConfirm(
        'tmc.admin.supplementary-equipment.discard.confirm-dialog.title',
        'tmc.admin.supplementary-equipment.discard.confirm-dialog.message'
      ).afterClosed().subscribe(confirm => {
        if (confirm) {
          this.initCreation();
        }
      });

    } else {
      this.initCreation();
    }
  }

  initCreation() {
    this.selectedEquipment = null;
    this.selectedDetails = this.createNewDetails();
    this.workingCopyDetails = JSON.parse(JSON.stringify(this.selectedDetails));
  }

  load() {
    this.isLoading = true;
    this.ses.getSupplementaryEquipment().subscribe(euqipments => {

      const newEquipment = new Array<SupplementaryEquipment>();
      for (const euqipment of euqipments) {
        newEquipment.push(new SupplementaryEquipment(euqipment));
      }

      this.euqipment = newEquipment;

      if (!this.selectedEquipment && this.euqipment.length > 0) {
        this.setSelectedWithoutCheck(this.euqipment[0]);
      }
      this.createTranslatedValuesOnList();
      this.isLoading = false;

    }, error => {
      console.error(error);
      this.isLoading = false;
    });
  }

  cancel() {
    if (this.workingCopyDetails.id) {
      this.setSelectedWithoutCheck(this.selectedEquipment);
    } else {
      this.workingCopyDetails = this.createNewDetails();
    }
    this.responseMessages = [];
  }

  get saveDisabled() {

    if (!this.didIdentifiersChange) {
      if (!this.isDirty()) {
        return true;
      }
    }

    if (this.workingCopyDetails.name.values.length == 0) {
      return true;
    }


    if (!this.workingCopyDetails.parameter1) {
      return true;
    }

    if (this.isLoading) {
      return true;
    }
    return false;
  }

  get cancelDisabled() {
    return this.isLoading || !this.isDirty();
  }

  isDirty(): boolean {
    return JSON.stringify(this.selectedDetails) !== JSON.stringify(this.workingCopyDetails);
  }

  createNewDetails() {
    const details = new SupplementaryEquipmentDetails();
    details.name = new DictionaryEntry();
    details.parameter1 = new DictionaryEntry();
    details.parameter1Type = ParameterType.NONE;
    details.parameter2 = new DictionaryEntry();
    details.parameter2Type = ParameterType.NONE;
    details.parameter3 = new DictionaryEntry();
    details.parameter3Type = ParameterType.NONE;
    details.identifiers = new Array<SupplementaryEquipmentIdentifier>();
    return details;
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as SupplementaryEquipment);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as SupplementaryEquipment);
  }

  addNewIdentifier(identifier: SupplementaryEquipmentIdentifier) {
    if (this.workingCopyDetails.identifiers == null) {
      this.workingCopyDetails.identifiers = new Array<SupplementaryEquipmentIdentifier>();
    }
    this.workingCopyDetails.identifiers.push(identifier);
    this.didIdentifiersChange = true;
  }

  changeActiveOfIdentfier(changedIdentifier: SupplementaryEquipmentIdentifier) {
    const index = this.workingCopyDetails.identifiers.findIndex(identifier => identifier.identifier == changedIdentifier.identifier);
    this.workingCopyDetails.identifiers[index] = changedIdentifier;
    this.didIdentifiersChange = true;
  }

  deleteIdentifier(identifier: SupplementaryEquipmentIdentifier) {
    this.workingCopyDetails.identifiers = this.workingCopyDetails.identifiers.filter(
      (oldIdentifier) => oldIdentifier.identifier != identifier.identifier
    );
    console.log("Identifiers: ", this.workingCopyDetails.identifiers);
    // console.log("I", test)
    this.didIdentifiersChange = true;
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
